//frontend/src/components/Chat/AdminMessages.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Form, Button, Badge, Dropdown, Alert, Modal } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { FaSearch, FaPaperPlane, FaThumbtack, FaEnvelope, FaEllipsisV, FaPaperclip, FaTimes, FaDownload, FaPlus, FaUser, FaHeadset } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';
import '../../styles/ModernMessages.css';

const AdminMessages = () => {
    const { user } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [totalUnread, setTotalUnread] = useState(0);
    
    const [showNewConversationModal, setShowNewConversationModal] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    
    const messagesEndRef = useRef(null);
    const chatMessagesRef = useRef(null);
    const fileInputRef = useRef(null);

    const loadMessages = useCallback(async (conversationId) => {
        try {
            const response = await api.get(`/conversations/${conversationId}`);
            setMessages(Array.isArray(response.data.conversation.messages) ? response.data.conversation.messages : []);
        } catch (err) {
            console.error('Error loading messages:', err);
        }
    }, []);

    const loadConversations = useCallback(async (isInitialLoad = false) => {
        if (isInitialLoad) {
            setLoading(true);
        } else {
            setIsRefreshing(true);
        }
        
        try {
            const response = await api.get('/conversations/');
            const data = response.data.conversations || [];
            
            console.log('Conversations chargées:', data);
            
            const sortedConversations = data.sort((a, b) => {
                if (a.is_pinned && !b.is_pinned) return -1;
                if (!a.is_pinned && b.is_pinned) return 1;
                return new Date(b.updated_at) - new Date(a.updated_at);
            });
            
            setConversations(sortedConversations);
            setTotalUnread(sortedConversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0));

            if (isInitialLoad && sortedConversations.length > 0 && !selectedConversation) {
                setSelectedConversation(sortedConversations[0]);
            }
            
            if (selectedConversation && !isInitialLoad) {
                await loadMessages(selectedConversation.id);
            }
        } catch (err) {
            console.error('Error loading conversations:', err);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [selectedConversation, loadMessages]);

    const markConversationAsRead = useCallback(async (conversationId) => {
        try {
            await api.post(`/conversations/${conversationId}/mark-as-read`);
            setConversations(prev => {
                const updated = prev.map(conv =>
                    conv.id === conversationId
                        ? { ...conv, unread_count: 0 }
                        : conv
                );
                setTotalUnread(updated.reduce((sum, conv) => sum + (conv.unread_count || 0), 0));
                return updated;
            });
        } catch (err) {
            console.error('Error marking as read:', err);
        }
    }, []);

    const loadAllUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await api.get('/admin/users');
            const users = response.data.users.data || [];

            const regularUsers = users.filter(u => u.role === 'user');
            setAllUsers(regularUsers);
        } catch (err) {
            console.error('Error loading users:', err);
            toast.error('Erreur lors du chargement des utilisateurs');
        } finally {
            setLoadingUsers(false);
        }
    };

    useEffect(() => {
        loadConversations(true);
        
        const interval = setInterval(() => {
            if (!sending) {
                loadConversations(false);
            }
        }, 30000);
        
        return () => clearInterval(interval);
    }, [sending, loadConversations]);

    useEffect(() => {
        if (selectedConversation && selectedConversation.id) {
            loadMessages(selectedConversation.id);
            markConversationAsRead(selectedConversation.id);
        }
    }, [selectedConversation, loadMessages, markConversationAsRead]);

    useEffect(() => {
        if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
    }, [messages]);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toast.error('Le fichier ne doit pas dépasser 10MB');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleTogglePin = async (conversationId) => {
        try {
            await api.put(`/conversations/${conversationId}/pin`);
            loadConversations(false);
            toast.success('Conversation épinglée/désépinglée');
        } catch (err) {
            console.error('Error toggling pin:', err);
            toast.error("Erreur lors de l'épinglage");
        }
    };

    const handleMarkAsUnread = async (conversationId) => {
        try {
            await api.post(`/chat/conversations/${conversationId}/mark_as_unread/`);
            loadConversations(false);
            toast.success('Marqué comme non lu');
        } catch (err) {
            console.error('Error marking as unread:', err);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

        setSending(true);

        try {
            const formData = new FormData();
            formData.append('content', newMessage);
            if (selectedFile) {
                formData.append('file', selectedFile);
            }

            await api.post(`/conversations/${selectedConversation.id}/messages`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setNewMessage('');
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            
            await loadMessages(selectedConversation.id);
            await loadConversations(false);
        } catch (err) {
            toast.error("Erreur lors de l'envoi du message");
        } finally {
            setSending(false);
        }
    };

    const handleShowNewConversationModal = () => {
        setShowNewConversationModal(true);
        loadAllUsers();
    };

    const handleStartNewConversation = async (userId) => {
        try {
            const existingConv = conversations.find(conv => conv.user.id === userId);
            
            if (existingConv) {
                setSelectedConversation(existingConv);
                setShowNewConversationModal(false);
                toast.info('Conversation existante sélectionnée');
                return;
            }

            const response = await api.post('/conversations/create-for-user', {
                user_id: userId
            });
            
            await loadConversations(true);
            setSelectedConversation(response.data.conversation);
            setShowNewConversationModal(false);
            toast.success('Nouvelle conversation créée');
        } catch (err) {
            console.error('Error creating conversation:', err);
            toast.error('Erreur lors de la création de la conversation');
        }
    };

    const getUserInfo = (conversation) => {
        if (!conversation.user) {
            return {
                name: 'Utilisateur inconnu',
                email: '',
                role: 'user'
            };
        }
        return {
            name: `${conversation.user.first_name} ${conversation.user.last_name}`,
            email: conversation.user.email,
            role: conversation.user.role
        };
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        
        if (date.toDateString() === today.toDateString()) {
            return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const getFileIcon = (fileName) => {
        const ext = fileName?.split('.').pop()?.toLowerCase();
        const icons = {
            pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
            jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️', zip: '📦', rar: '📦'
        };
        return icons[ext] || '📎';
    };

    const filteredConversations = conversations.filter(conv => {
        const userInfo = getUserInfo(conv);
        return userInfo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               userInfo.email.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const filteredUsers = allUsers.filter(u => {
        const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
        const email = u.email.toLowerCase();
        const search = userSearchTerm.toLowerCase();
        return fullName.includes(search) || email.includes(search);
    });

    const handleDeleteConversation = async (conversationId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette conversation ? Cette action est irréversible.')) {
            return;
        }

        try {
            await api.delete(`/chat/conversations/${conversationId}/`);
            setConversations(prev => prev.filter(conv => conv.id !== conversationId));
            if (selectedConversation?.id === conversationId) {
                setSelectedConversation(null);
                setMessages([]);
            }
            toast.success('Conversation supprimée avec succès');
        } catch (err) {
            console.error('Error deleting conversation:', err);
            toast.error('Erreur lors de la suppression de la conversation');
        }
    };

    if (loading) {
        return (
            <Container className="messages-loading">
                <div className="spinner-border text-primary"></div>
                <p className="mt-3">Chargement...</p>
            </Container>
        );
    }

    return (
        <div className="modern-messages-container">
            <div className="modern-messages-sidebar">
                <div className="sidebar-header">
                    <div className="sidebar-title">
                        <h2>
                            <FaEnvelope className="me-2" />
                            Messages
                            {totalUnread > 0 && <Badge bg="danger" className="ms-2">{totalUnread}</Badge>}
                        </h2>
                        <Button 
                            variant="primary" 
                            size="sm"
                            onClick={handleShowNewConversationModal}
                            className="btn-new-conversation"
                            title="Nouvelle conversation"
                        >
                            <FaPlus />
                        </Button>
                    </div>
                    {isRefreshing && <div className="refresh-indicator"></div>}
                </div>

                <div className="search-box">
                    <FaSearch className="search-icon" />
                    <Form.Control
                        type="text"
                        placeholder="Rechercher un utilisateur..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="conversations-list">
                    {filteredConversations.length > 0 ? (
                        filteredConversations.map((conv) => {
                            const userInfo = getUserInfo(conv);
                            return (
                                <div
                                    key={conv.id}
                                    className={`conversation-card ${selectedConversation?.id === conv.id ? 'active' : ''} ${conv.unread_count > 0 ? 'unread' : ''}`}
                                    onClick={() => setSelectedConversation(conv)}
                                >
                                    {conv.is_pinned && (
                                        <div className="pin-badge">
                                            <FaThumbtack />
                                        </div>
                                    )}
                                    
                                    <div className="conversation-avatar">
                                        <div className="avatar-user">
                                            {userInfo.name.charAt(0).toUpperCase()}
                                        </div>
                                    </div>
                                    
                                    <div className="conversation-info">
                                        <div className="conversation-header">
                                            <h4 className="conversation-name">
                                                {userInfo.name}
                                            </h4>
                                            <span className="conversation-time">
                                                {formatTime(conv.updated_at)}
                                            </span>
                                        </div>
                                        <p className="conversation-preview">
                                            {conv.last_message?.content || 'Nouvelle conversation'}
                                        </p>
                                        <small className="text-muted">{userInfo.email}</small>
                                    </div>
                                    
                                    {conv.unread_count > 0 && (
                                        <Badge bg="danger" className="unread-badge">
                                            {conv.unread_count}
                                        </Badge>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className="empty-state">
                            <FaEnvelope size={50} className="text-muted" />
                            <p className="mt-3">Aucune conversation</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="modern-chat-area">
                {selectedConversation ? (
                    <>
                        <div className="chat-header">
                            <div className="chat-user-info">
                                <div className="chat-avatar">
                                    <div className="avatar-user">
                                        {getUserInfo(selectedConversation).name.charAt(0).toUpperCase()}
                                    </div>
                                </div>
                                <div>
                                    <h3>{getUserInfo(selectedConversation).name}</h3>
                                    <p className="status-text">
                                        {getUserInfo(selectedConversation).email}
                                    </p>
                                </div>
                            </div>
                            
                            <Dropdown align="end">
                                <Dropdown.Toggle variant="link" className="btn-icon-header">
                                    <FaEllipsisV />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => handleTogglePin(selectedConversation.id)}>
                                        <FaThumbtack className="me-2" />
                                        {selectedConversation.is_pinned ? 'Désépingler' : 'Épingler'}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        <div className="chat-messages-area" ref={chatMessagesRef}>
                            {messages.map((msg) => {
                                const isSent = msg.sender?.id === user.id;
                                return (
                                    <div key={msg.id} className={`d-flex mb-3 ${isSent ? 'justify-content-end' : 'justify-content-start'}`}>
                                        {!isSent && (
                                            <div className="me-2">
                                                <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white" style={{ width: '35px', height: '35px' }}>
                                                    <FaHeadset />
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ maxWidth: '70%' }}>
                                            <div
                                                className="message-bubble"
                                                style={{
                                                    backgroundColor: isSent ? '#007bff' : '#ffffff',
                                                    color: isSent ? '#ffffff' : '#333333',
                                                    border: isSent ? 'none' : '1px solid #e9ecef',
                                                    borderRadius: isSent ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                                    padding: '12px 16px',
                                                    boxShadow: isSent ? '0 2px 8px rgba(0, 123, 255, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                    marginBottom: '4px',
                                                    wordWrap: 'break-word'
                                                }}
                                            >
                                                {msg.content && <p className="mb-0" style={{ fontSize: '14px', lineHeight: '1.4' }}>{msg.content}</p>}

                                                {msg.file && (
                                                    <div
                                                        className="file-attachment mt-2 p-2 rounded"
                                                        style={{
                                                            backgroundColor: isSent ? 'rgba(255, 255, 255, 0.2)' : '#f8f9fa',
                                                            border: isSent ? 'none' : '1px solid #dee2e6'
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <span className="me-2 fs-4">{getFileIcon(msg.file_name)}</span>
                                                            <div className="flex-grow-1">
                                                                <small className="d-block fw-bold" style={{ color: isSent ? '#ffffff' : '#333333' }}>{msg.file_name}</small>
                                                                <small style={{ color: isSent ? 'rgba(255, 255, 255, 0.7)' : '#6c757d' }}>
                                                                    {msg.file_size ? `${msg.file_size} MB` : 'Fichier'}
                                                                </small>
                                                            </div>
                                                            <a
                                                                href={`${api.defaults.baseURL.replace('/api', '')}${msg.file}`}
                                                                download
                                                                className={`btn btn-sm ${isSent ? 'btn-light' : 'btn-outline-primary'}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ fontSize: '12px', padding: '4px 8px' }}
                                                            >
                                                                <FaDownload />
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <small
                                                className={`d-block mt-1 ${isSent ? 'text-end' : 'text-start'} text-muted`}
                                                style={{ fontSize: '12px' }}
                                            >
                                                {formatTime(msg.created_at)}
                                            </small>
                                        </div>

                                        {isSent && (
                                            <div className="ms-2">
                                                <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white" style={{ width: '35px', height: '35px', fontSize: '14px' }}>
                                                    {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {selectedFile && (
                            <div className="px-3 pt-2">
                                <Alert variant="info" className="mb-0 py-2">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <span className="me-2 fs-4">{getFileIcon(selectedFile.name)}</span>
                                            <div>
                                                <small className="fw-bold d-block">{selectedFile.name}</small>
                                                <small className="text-muted">{formatFileSize(selectedFile.size)}</small>
                                            </div>
                                        </div>
                                        <Button variant="link" size="sm" className="text-danger" onClick={handleRemoveFile}>
                                            <FaTimes />
                                        </Button>
                                    </div>
                                </Alert>
                            </div>
                        )}

                        <Form className="chat-input-area" onSubmit={handleSendMessage}>
                            <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.rar" />
                            
                            <Button variant="outline-secondary" onClick={() => fileInputRef.current?.click()} className="btn-attach-file me-2" title="Joindre un fichier">
                                <FaPaperclip />
                            </Button>
                            
                            <Form.Control type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Écrivez votre message..." disabled={sending} className="message-input" />
                            
                            <Button type="submit" className="btn-send-message" disabled={(!newMessage.trim() && !selectedFile) || sending}>
                                {sending ? <div className="spinner-border spinner-border-sm"></div> : <FaPaperPlane />}
                            </Button>
                        </Form>
                    </>
                ) : (
                    <div className="no-conversation-selected">
                        <FaEnvelope size={80} className="text-muted" />
                        <h4 className="mt-4">Sélectionnez une conversation</h4>
                        <p className="text-muted">
                            Choisissez une conversation dans la liste ou créez-en une nouvelle
                        </p>
                    </div>
                )}
            </div>

            <Modal show={showNewConversationModal} onHide={() => setShowNewConversationModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaPlus className="me-2" />
                        Nouvelle Conversation
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Rechercher un utilisateur</Form.Label>
                        <div className="position-relative">
                            <FaSearch className="position-absolute" style={{ left: '12px', top: '12px', color: '#6c757d' }} />
                            <Form.Control
                                type="text"
                                placeholder="Nom ou email..."
                                value={userSearchTerm}
                                onChange={(e) => setUserSearchTerm(e.target.value)}
                                style={{ paddingLeft: '35px' }}
                            />
                        </div>
                    </Form.Group>

                    {loadingUsers ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary"></div>
                            <p className="mt-2">Chargement des utilisateurs...</p>
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <Alert variant="info">Aucun utilisateur trouvé</Alert>
                    ) : (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {filteredUsers.map((u) => (
                                <div
                                    key={u.id}
                                    className="d-flex align-items-center p-3 border-bottom"
                                    style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                                    onClick={() => handleStartNewConversation(u.id)}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                    <div className="me-3">
                                        <div 
                                            className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center"
                                            style={{ width: '50px', height: '50px' }}
                                        >
                                            <FaUser className="text-primary" size={20} />
                                        </div>
                                    </div>
                                    <div className="flex-grow-1">
                                        <h6 className="mb-0">{u.first_name} {u.last_name}</h6>
                                        <small className="text-muted">{u.email}</small>
                                    </div>
                                    <Button variant="outline-primary" size="sm">
                                        Sélectionner
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AdminMessages;